import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import app, { useFirebaseEmulators } from "../../firebase";
import { useSession } from "../../context/SessionContext";
import { createManagedAccount } from "../../services/studentAccountCreation";
import {
  declineStudentApplication,
  getPendingStudentDeclines,
  previewStudentApplicationDecline,
  recoverStudentApplicationDecline
} from "../../services/studentApplicationDecline";

// helper: accept string, Firestore Timestamp, or Date and return YYYY-MM-DD (or empty string)
function formatDate(value) {
  if (!value) return "";
  // Firestore Timestamp
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString().split("T")[0];
    } catch (e) {
      return "";
    }
  }
  // Date instance
  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }
  // string (ISO expected)
  if (typeof value === "string") {
    return value.split("T")[0];
  }
  return "";
}

function formatDateTime(value) {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    try {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime())
        ? date.toISOString().replace("T", " ").slice(0, 19)
        : "";
    } catch (e) {
      return "";
    }
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime())
      ? value.toISOString().replace("T", " ").slice(0, 19)
      : "";
  }

  return typeof value === "string" ? value.replace("T", " ").slice(0, 19) : "";
}

export default function ReviewApplication() {
  const { currentUser } = useSession();
  const [reviewList, setReviewList] = useState([]); // students to review
  const [approvedList, setApprovedList] = useState([]); // students already approved
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [genId, setGenId] = useState("");
  const [genPass, setGenPass] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState("");
  const [tab, setTab] = useState("review");
  const [declineStudent, setDeclineStudent] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineDetails, setDeclineDetails] = useState("");
  const [declinePreview, setDeclinePreview] = useState(null);
  const [declinePreviewError, setDeclinePreviewError] = useState("");
  const [declinePreviewLoading, setDeclinePreviewLoading] = useState(false);
  const [declineError, setDeclineError] = useState("");
  const [declineNotice, setDeclineNotice] = useState("");
  const [declining, setDeclining] = useState(false);
  const [pendingDeclines, setPendingDeclines] = useState([]);
  const [uncertainDeclineIds, setUncertainDeclineIds] = useState([]);
  // Filters and pagination (mirror AssessmentApproval UI)
  const rowsPerPage = 20;
  // Review tab filters
  const [reviewNameFilter, setReviewNameFilter] = useState("");
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState("all");
  const [reviewProgramFilter, setReviewProgramFilter] = useState("all");
  const [reviewDateFilter, setReviewDateFilter] = useState("");
  const [reviewPage, setReviewPage] = useState(1);
  // Approved tab filters
  const [approvedIdFilter, setApprovedIdFilter] = useState("");
  const [approvedNameFilter, setApprovedNameFilter] = useState("");
  const [approvedCategoryFilter, setApprovedCategoryFilter] = useState("all");
  const [approvedProgramFilter, setApprovedProgramFilter] = useState("all");
  const [approvedDateFilter, setApprovedDateFilter] = useState("");
  const [approvedPage, setApprovedPage] = useState(1);

  const clearAllFilters = () => {
    setReviewNameFilter("");
    setReviewCategoryFilter("all");
    setReviewProgramFilter("all");
    setReviewDateFilter("");
    setReviewPage(1);
    setApprovedIdFilter("");
    setApprovedNameFilter("");
    setApprovedCategoryFilter("all");
    setApprovedProgramFilter("all");
    setApprovedDateFilter("");
    setApprovedPage(1);
  };

  useEffect(() => {
    const db = getFirestore(app);

    const fetchPrograms = async () => {
      try {
        const snapshot = await getDocs(collection(db, "departments"));
        const availablePrograms = [...new Set(
          snapshot.docs
            .map(docSnap => docSnap.data().name?.trim())
            .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));
        setPrograms(availablePrograms);
      } catch (err) {
        console.error("Error fetching programs:", err);
        setPrograms([]);
      }
    };
    fetchPrograms();
    
    // Listen to unapproved students (no idNumber)
    const unsubscribeReview = onSnapshot(collection(db, "students"), (snap) => {
      const review = [];
      snap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (!data.idNumber) {
          review.push({ ...data, id: docSnap.id });
        }
      });
      setReviewList(review);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching unapproved students:", err);
      setReviewList([]);
      setLoading(false);
    });

    // Listen to approved students (have idNumber)
    const unsubscribeApproved = onSnapshot(collection(db, "students"), (snap) => {
      const approved = [];
      snap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.idNumber) {
          approved.push({ ...data, id: docSnap.id });
        }
      });
      setApprovedList(approved);
    }, (err) => {
      console.error("Error fetching approved students:", err);
      setApprovedList([]);
    });

    return () => {
      unsubscribeReview();
      unsubscribeApproved();
    };
  }, []);

  // Filtered and paginated lists
  const filteredReview = reviewList.filter(stu => {
    const nameMatch = `${stu.firstName ?? ""} ${stu.middleName ?? ""} ${stu.lastName ?? ""}`.toLowerCase().includes(reviewNameFilter.toLowerCase());
    const categoryMatch = reviewCategoryFilter === "all" || (stu.category ?? "").toLowerCase() === reviewCategoryFilter.toLowerCase();
    const programMatch = reviewProgramFilter === "all" || (stu.program ?? "").trim().toLowerCase() === reviewProgramFilter.toLowerCase();
    const dateMatch = !reviewDateFilter || formatDate(stu.createdAt) === reviewDateFilter;
    return nameMatch && categoryMatch && programMatch && dateMatch;
  });
  const reviewTotalPages = Math.max(1, Math.ceil(filteredReview.length / rowsPerPage));
  const displayedReview = filteredReview.slice((reviewPage - 1) * rowsPerPage, reviewPage * rowsPerPage);

  const filteredApproved = approvedList.filter(stu => {
    const idMatch = (stu.idNumber ?? "").toString().includes(approvedIdFilter);
    const nameMatch = `${stu.firstName ?? ""} ${stu.middleName ?? ""} ${stu.lastName ?? ""}`.toLowerCase().includes(approvedNameFilter.toLowerCase());
    const categoryMatch = approvedCategoryFilter === "all" || (stu.category ?? "").toLowerCase() === approvedCategoryFilter.toLowerCase();
    const programMatch = approvedProgramFilter === "all" || (stu.program ?? "").trim().toLowerCase() === approvedProgramFilter.toLowerCase();
    const dateMatch = !approvedDateFilter || formatDate(stu.createdAt) === approvedDateFilter;
    return idMatch && nameMatch && categoryMatch && programMatch && dateMatch;
  });
  const approvedTotalPages = Math.max(1, Math.ceil(filteredApproved.length / rowsPerPage));
  const displayedApproved = filteredApproved.slice((approvedPage - 1) * rowsPerPage, approvedPage * rowsPerPage);

  useEffect(() => {
    setPendingDeclines(getPendingStudentDeclines());
  }, []);

  useEffect(() => {
    if (!declineStudent || !declineReason) {
      setDeclinePreview(null);
      setDeclinePreviewError("");
      setDeclinePreviewLoading(false);
      return undefined;
    }
    let active = true;
    setDeclinePreview(null);
    setDeclinePreviewError("");
    setDeclinePreviewLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const preview = await previewStudentApplicationDecline({
          user: currentUser,
          studentDocumentId: declineStudent.id,
          reason: declineReason,
          details: declineDetails
        });
        if (active) setDeclinePreview(preview);
      } catch (error) {
        if (active) setDeclinePreviewError(error.message || "Unable to prepare the email preview.");
      } finally {
        if (active) setDeclinePreviewLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [currentUser, declineStudent, declineReason, declineDetails]);

  const closeDeclineDialog = () => {
    if (declining) return;
    setDeclineStudent(null);
    setDeclineReason("");
    setDeclineDetails("");
    setDeclinePreview(null);
    setDeclinePreviewError("");
    setDeclineError("");
  };

  const applyDeclineResult = (studentDocumentId, result) => {
    setPendingDeclines(getPendingStudentDeclines());
    if (result.outcome === "recovery_pending") {
      setDeclineError(result.error?.message || "The decline is incomplete and needs recovery.");
      return false;
    }
    setReviewList(previous => previous.filter(student => student.id !== studentDocumentId));
    setApprovedList(previous => previous.filter(student => student.id !== studentDocumentId));
    setPendingDeclines(getPendingStudentDeclines());
    setDeclineNotice(result.accountReset ?
      "Application declined, email sent, and the linked student account reset." :
      "Application declined and email sent. No linked Authentication account existed; the application record was removed.");
    setDeclineStudent(null);
    setSelected(null);
    setDeclineError("");
    return true;
  };

  const submitDecline = async () => {
    if (!declineStudent || !declineReason || declining) return;
    setDeclining(true);
    setDeclineError("");
    try {
      const result = await declineStudentApplication({
        user: currentUser,
        studentDocumentId: declineStudent.id,
        reason: declineReason,
        details: declineDetails
      });
      applyDeclineResult(declineStudent.id, result);
    } catch (error) {
      setDeclineError(error.message || "The application could not be declined.");
      if (error.code === "email_delivery_uncertain") {
        setUncertainDeclineIds(previous => (
          previous.includes(declineStudent.id) ? previous : [...previous, declineStudent.id]
        ));
      }
      setPendingDeclines(getPendingStudentDeclines());
    } finally {
      setDeclining(false);
    }
  };

  const recoverDecline = async (pending, resolveEmailDelivery) => {
    if (declining) return;
    setDeclining(true);
    setDeclineError("");
    try {
      const result = await recoverStudentApplicationDecline({
        user: currentUser,
        operationId: pending.operationId,
        studentDocumentId: pending.studentDocumentId,
        resolveEmailDelivery
      });
      applyDeclineResult(pending.studentDocumentId, result);
      if (result.outcome !== "recovery_pending") {
        setUncertainDeclineIds(previous => previous.filter(id => id !== pending.studentDocumentId));
      }
    } catch (error) {
      if (error.code === "email_delivery_uncertain") {
        setUncertainDeclineIds(previous => (
          previous.includes(pending.studentDocumentId) ? previous : [...previous, pending.studentDocumentId]
        ));
      }
      setDeclineError(error.message || "The decline recovery could not be completed.");
    } finally {
      setPendingDeclines(getPendingStudentDeclines());
      setDeclining(false);
    }
  };

  // Move student to approved after sending credentials
  const handleApprove = (stu, id, pass) => {
    setReviewList(prev => prev.filter(s => s.id !== stu.id));
    // Don't manually add to approvedList - let the listener handle it
    setSelected(null);
    setGenId("");
    setGenPass("");
  };

  return (
    <div className="review-applications">
      {declineNotice && (
        <div role="status" style={{ marginBottom: 12, padding: 12, background: "#edf7ed", color: "#155c2b", borderRadius: 6 }}>
          {declineNotice}
          <button onClick={() => setDeclineNotice("")} style={{ marginLeft: 12 }}>Dismiss</button>
        </div>
      )}
      {pendingDeclines.length > 0 && (
        <div role="status" style={{ marginBottom: 12, padding: 12, background: "#fff2f0", color: "#8a1c1c", borderRadius: 6 }}>
          <div>One or more application declines need recovery.</div>
          {declineError && <div style={{ marginTop: 6 }}>{declineError}</div>}
          {pendingDeclines.map(pending => (
            <div key={pending.operationId} style={{ marginTop: 8 }}>
              <span>Application {pending.studentDocumentId}: </span>
              <button onClick={() => recoverDecline(pending)} disabled={declining}>
                {declining ? "Recovering..." : "Retry decline"}
              </button>
              {uncertainDeclineIds.includes(pending.studentDocumentId) && (
                <>
                  <button
                    onClick={() => recoverDecline(pending, "sent")}
                    disabled={declining}
                    style={{ marginLeft: 8 }}
                  >
                    Email was sent — continue reset
                  </button>
                  <button
                    onClick={() => recoverDecline(pending, "not_sent")}
                    disabled={declining}
                    style={{ marginLeft: 8 }}
                  >
                    Email was not sent — retry email
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <button onClick={() => setTab("review")}
          style={{ background: tab === "review" ? "#155c2b" : "#fff", color: tab === "review" ? "#fff" : "#222", border: '1px solid #155c2b', borderRadius: 6, padding: '8px 24px', fontWeight: 600, cursor: 'pointer' }}>
          Review Application
        </button>
        <button onClick={() => setTab("approved")}
          style={{ background: tab === "approved" ? "#155c2b" : "#fff", color: tab === "approved" ? "#fff" : "#222", border: '1px solid #155c2b', borderRadius: 6, padding: '8px 24px', fontWeight: 600, cursor: 'pointer' }}>
          Approved Application
        </button>
      </div>
      {tab === "review" && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Filter by Name"
              value={reviewNameFilter}
              maxLength={20}
              onChange={(e) => { setReviewNameFilter(e.target.value.replace(/[^\p{L}\s]/gu, "").slice(0, 20)); setReviewPage(1); }}
              style={{ padding: 10, minWidth: 220, borderRadius: 6, border: '1px solid #ccc' }}
            />
            <select
              value={reviewCategoryFilter}
              onChange={(e) => { setReviewCategoryFilter(e.target.value); setReviewPage(1); }}
              style={{ padding: 10, minWidth: 180, borderRadius: 6, border: '1px solid #ccc' }}
            >
              <option value="all">All Categories</option>
              <option value="New Student">New Student</option>
              <option value="Transferee">Transferee</option>
            </select>
            <select
              value={reviewProgramFilter}
              onChange={(e) => { setReviewProgramFilter(e.target.value); setReviewPage(1); }}
              style={{ padding: 10, minWidth: 200, borderRadius: 6, border: '1px solid #ccc' }}
            >
              <option value="all">All Programs</option>
              {programs.map(program => <option key={program} value={program}>{program}</option>)}
            </select>
            <input
              type="date"
              value={reviewDateFilter}
              onChange={(e) => { setReviewDateFilter(e.target.value); setReviewPage(1); }}
              style={{ padding: 10, minWidth: 180, borderRadius: 6, border: '1px solid #ccc' }}
            />
            <button onClick={clearAllFilters}
              style={{ background: "#e67e22", color: "#fff", border: '2px solid #e67e22', borderRadius: 6, padding: '10px 20px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>
              Clear All
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 24 }}>
            <thead>
              <tr style={{ background: "#e0e0e0" }}>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Name</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Category</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Program</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Date Submitted</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedReview.map(stu => (
                <tr key={stu.id}>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.firstName} {stu.middleNameNA ? '' : stu.middleName} {stu.lastName}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.category}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.program}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{formatDate(stu.createdAt)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center" }}>
                    <button onClick={() => setSelected(stu)} style={{ background: "#155c2b", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Review</button>
                  </td>
                </tr>
              ))}
              {reviewList.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "#888", padding: 16 }}>No applications to review.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <div style={{ color: '#555' }}>
              Showing {Math.min((reviewPage - 1) * rowsPerPage + 1, filteredReview.length || 1)} - {Math.min(reviewPage * rowsPerPage, filteredReview.length)} of {filteredReview.length}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setReviewPage(p => Math.max(1, p - 1))} disabled={reviewPage === 1} style={{ padding: '6px 10px' }}>Prev</button>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{Array.from({ length: reviewTotalPages }).map((_, i) => (
                <button key={i} onClick={() => setReviewPage(i + 1)} style={{ padding: '6px 10px', background: reviewPage === i + 1 ? '#155c2b' : undefined, color: reviewPage === i + 1 ? '#fff' : undefined }}>{i + 1}</button>
              ))}</div>
              <button onClick={() => setReviewPage(p => Math.min(reviewTotalPages, p + 1))} disabled={reviewPage === reviewTotalPages} style={{ padding: '6px 10px' }}>Next</button>
            </div>
          </div>
          {/* Student Details Modal/Section */}
          {selected && (
            <div style={{ position: "fixed", inset: 0, padding: 16, boxSizing: "border-box", background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
              <div style={{ background: "#fff", padding: 24, borderRadius: 10, width: "100%", maxWidth: 560, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxSizing: "border-box", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}>
                <h3 style={{ margin: "0 0 6px", color: "#155c2b", fontSize: 21 }}>Student Details</h3>
                <p style={{ margin: "0 0 20px", color: "#666", fontSize: 14 }}>Review the application details and required documents.</p>

                <h4 style={{ margin: "0 0 10px", fontSize: 15 }}>Applicant information</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: "10px 16px", marginBottom: 22 }}>
                  {[
                    ["Name", `${selected.firstName || ""} ${selected.middleNameNA ? "" : selected.middleName || ""} ${selected.lastName || ""}`.trim()],
                    ["Email", selected.email],
                    ["Category", selected.category],
                    ["Program", selected.program],
                    ["Gender", selected.gender],
                    ["Date of birth", selected.dob],
                    ["Mobile", selected.mobile],
                    ["Submitted", formatDateTime(selected.createdAt)]
                  ].map(([label, value]) => (
                    <div key={label} style={{ minWidth: 0 }}>
                      <div style={{ color: "#666", fontSize: 12, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 14, overflowWrap: "anywhere" }}>{value || "Not provided"}</div>
                    </div>
                  ))}
                </div>

                <h4 style={{ margin: "0 0 10px", fontSize: 15 }}>Submitted documents</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: 8, marginBottom: 20 }}>
                  {[
                    ["2x2 picture", selected.picture],
                    ["Birth/Marriage Certificate", selected.birthCert],
                    ["School/Govt ID", selected.schoolId],
                    ["Copy of Grades", selected.grades]
                  ].map(([label, url]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13 }}>
                      <span>{label}</span>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#155c2b", fontWeight: 600, whiteSpace: "nowrap" }}>View</a>
                      ) : (
                        <span style={{ color: "#777", whiteSpace: "nowrap" }}>Not uploaded</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* ID Number and Password Generation */}
                <div style={{ margin: "0 0 20px", borderTop: "1px solid #e5e7eb", paddingTop: 18 }}>
                  <h4 style={{ margin: "0 0 12px", fontSize: 15 }}>Student account</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: 12, marginBottom: 14 }}>
                    <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: 600 }}>
                      ID number
                      <input type="text" value={genId} readOnly aria-label="Generated ID number" style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", border: "1px solid #cbd5e1", borderRadius: 5, background: "#f8fafc" }} />
                    </label>
                    <label style={{ display: "grid", gap: 5, fontSize: 13, fontWeight: 600 }}>
                      Password
                      <input type="password" value={genPass} readOnly aria-label="Generated password" style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", border: "1px solid #cbd5e1", borderRadius: 5, background: "#f8fafc" }} />
                    </label>
                  </div>
                  <button
                    style={{ padding: "9px 16px", borderRadius: 6, background: sending ? "#aaa" : "#00875a", color: "#fff", border: "none", fontWeight: 600, cursor: sending ? "wait" : "pointer" }}
                    disabled={sending}
                    onClick={async () => {
                      setSendMsg("");
                      // Generate credentials
                      const id = Array.from({length:8},()=>Math.floor(Math.random()*10)).join("");
                      const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                      const lower = "abcdefghijklmnopqrstuvwxyz";
                      const nums = "0123456789";
                      function getRandom(arr) { return arr[Math.floor(Math.random()*arr.length)]; }
                      let passArr = [getRandom(upper), getRandom(nums)];
                      let all = upper + lower + nums;
                      while (passArr.length < 8) passArr.push(getRandom(all));
                      const pass = passArr.sort(() => Math.random() - 0.5).join("");
                      setGenId(id);
                      setGenPass(pass);

                      setSending(true);
                      if (useFirebaseEmulators) {
                        setSendMsg("Emulator mode: credentials email was not sent.");
                      } else try {
                        // Send email via cloud function
                        const res = await fetch("https://sendapprovalemail-ag2nilnpda-uc.a.run.app", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            to: selected.email,
                            name: `${selected.lastName}, ${selected.firstName}`,
                            idNumber: id,
                            program: selected.program,
                            password: pass,
                            template: "loginApproval"
                          })
                        });
                        if (!res.ok) {
                          const errorText = await res.text();
                          setSendMsg("❌ Failed to send email: " + errorText);
                          console.error("Email response error:", errorText);
                          setSending(false);
                          return; // STOP HERE - don't proceed to auth creation
                        }
                        setSendMsg("✅ Email sent successfully!");
                      } catch (e) {
                        console.error("send email error", e);
                        setSendMsg("❌ Failed to send email: " + e.message);
                        setSending(false);
                        return; // STOP HERE - don't proceed to auth creation
                      }

                      try {
                        await createManagedAccount({
                          caller: currentUser,
                          email: selected.email,
                          displayName: `${selected.firstName || ""} ${selected.lastName || ""}`.trim(),
                          password: pass,
                          studentDocumentId: selected.id,
                          idNumber: id
                        });

                        // Move to approved list (existing helper)
                        handleApprove(selected, id, pass);
                        setSendMsg("✅ Student approved successfully! Credentials sent via email.");
                      } catch (e) {
                        console.error("Error finalizing student approval:", e);
                        setSendMsg("❌ Failed to finalize approval: " + (e.message || e));
                      } finally {
                        setSending(false);
                      }
                    }}
                  >{sending ? "Sending..." : "Generate & Send"}</button>
                  {sendMsg && <div role="status" style={{ marginTop: 10, color: sendMsg.includes("success") ? "#147a3d" : "#a4261b", fontSize: 13, overflowWrap: "anywhere" }}>{sendMsg}</div>}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                  {!selected._approved && (
                    <button
                      onClick={() => {
                        setDeclineStudent(selected);
                        setDeclineReason("");
                        setDeclineDetails("");
                        setDeclineError("");
                        setSelected(null);
                        setGenId("");
                        setGenPass("");
                      }}
                      style={{ padding: "9px 16px", borderRadius: 6, background: "#c0392b", color: "#fff", border: "none", fontWeight: 600, cursor: "pointer" }}
                    >
                      Decline Application
                    </button>
                  )}
                  <button onClick={() => { setSelected(null); setGenId(""); setGenPass(""); }} style={{ padding: "9px 16px", borderRadius: 6, background: "#f3f4f6", color: "#222", border: "1px solid #d1d5db", fontWeight: 600, cursor: "pointer" }}>Close</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {tab === "approved" && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Filter by ID Number"
              value={approvedIdFilter}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => { setApprovedIdFilter(e.target.value.replace(/\D/g, "").slice(0, 10)); setApprovedPage(1); }}
              style={{ padding: 10, minWidth: 180, borderRadius: 6, border: '1px solid #ccc' }}
            />
            <input
              type="text"
              placeholder="Filter by Name"
              value={approvedNameFilter}
              maxLength={20}
              onChange={(e) => { setApprovedNameFilter(e.target.value.replace(/[^\p{L}\s]/gu, "").slice(0, 20)); setApprovedPage(1); }}
              style={{ padding: 10, minWidth: 220, borderRadius: 6, border: '1px solid #ccc' }}
            />
            <select
              value={approvedCategoryFilter}
              onChange={(e) => { setApprovedCategoryFilter(e.target.value); setApprovedPage(1); }}
              style={{ padding: 10, minWidth: 180, borderRadius: 6, border: '1px solid #ccc' }}
            >
              <option value="all">All Categories</option>
              <option value="New Student">New Student</option>
              <option value="Transferee">Transferee</option>
              <option value="Enrolled">Enrolled</option>
            </select>
            <select
              value={approvedProgramFilter}
              onChange={(e) => { setApprovedProgramFilter(e.target.value); setApprovedPage(1); }}
              style={{ padding: 10, minWidth: 200, borderRadius: 6, border: '1px solid #ccc' }}
            >
              <option value="all">All Programs</option>
              {programs.map(program => <option key={program} value={program}>{program}</option>)}
            </select>
            <input
              type="date"
              value={approvedDateFilter}
              onChange={(e) => { setApprovedDateFilter(e.target.value); setApprovedPage(1); }}
              style={{ padding: 10, minWidth: 180, borderRadius: 6, border: '1px solid #ccc' }}
            />
            <button onClick={clearAllFilters}
              style={{ background: "#e67e22", color: "#fff", border: '2px solid #e67e22', borderRadius: 6, padding: '10px 20px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>
              Clear All
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 24 }}>
            <thead>
              <tr style={{ background: "#e0e0e0" }}>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>ID Number</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Name</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Category</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Program</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Date Submitted</th>
                <th style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontWeight: 600, fontSize: 16 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedApproved.map(stu => (
                <tr key={stu.id}>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.idNumber}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.firstName} {stu.middleNameNA ? '' : stu.middleName} {stu.lastName}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.category}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{stu.program}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center", fontSize: 15 }}>{formatDate(stu.createdAt)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 12, textAlign: "center" }}>
                    <button onClick={() => setSelected({ ...stu, _approved: true })} style={{ background: "#155c2b", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", marginRight: 8 }}>View</button>
                  </td>
                </tr>
              ))}
              {approvedList.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "#888", padding: 16 }}>No approved applications.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <div style={{ color: '#555' }}>
              Showing {Math.min((approvedPage - 1) * rowsPerPage + 1, filteredApproved.length || 1)} - {Math.min(approvedPage * rowsPerPage, filteredApproved.length)} of {filteredApproved.length}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setApprovedPage(p => Math.max(1, p - 1))} disabled={approvedPage === 1} style={{ padding: '6px 10px' }}>Prev</button>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{Array.from({ length: approvedTotalPages }).map((_, i) => (
                <button key={i} onClick={() => setApprovedPage(i + 1)} style={{ padding: '6px 10px', background: approvedPage === i + 1 ? '#155c2b' : undefined, color: approvedPage === i + 1 ? '#fff' : undefined }}>{i + 1}</button>
              ))}</div>
              <button onClick={() => setApprovedPage(p => Math.min(approvedTotalPages, p + 1))} disabled={approvedPage === approvedTotalPages} style={{ padding: '6px 10px' }}>Next</button>
            </div>
          </div>
          {/* Approved Student Details Modal */}
          {selected && selected._approved && (
            <ApprovedStudentModal student={selected} onClose={() => setSelected(null)} />
          )}
        </>
      )}
      {declineStudent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="decline-application-title"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}
        >
          <div style={{ background: "#fff", padding: 28, borderRadius: 10, width: "min(720px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
            <h3 id="decline-application-title" style={{ marginTop: 0, color: "#155c2b" }}>Decline Application?</h3>
            <p><b>Student:</b> {[declineStudent.firstName, declineStudent.middleNameNA ? "" : declineStudent.middleName, declineStudent.lastName].filter(Boolean).join(" ")}</p>
            <p><b>Email:</b> {declineStudent.email}</p>
            <p><b>Program:</b> {declineStudent.program}</p>
            <label htmlFor="decline-reason" style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Reason for decline*</label>
            <select
              id="decline-reason"
              value={declineReason}
              onChange={event => setDeclineReason(event.target.value)}
              disabled={declining}
              style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", marginBottom: 16 }}
            >
              <option value="">Select a reason</option>
              <option value="Invalid Information">Invalid Information</option>
              <option value="Invalid or Unreadable Documents">Invalid or Unreadable Documents</option>
              <option value="Does Not Meet Admission Requirements">Does Not Meet Admission Requirements</option>
            </select>
            <label htmlFor="decline-details" style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Additional details (optional)</label>
            <textarea
              id="decline-details"
              value={declineDetails}
              maxLength={1000}
              onChange={event => setDeclineDetails(event.target.value)}
              disabled={declining}
              rows={3}
              style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box", marginBottom: 16 }}
            />
            <h4 style={{ marginBottom: 8 }}>Email Preview</h4>
            {declinePreviewLoading && <div role="status">Preparing preview...</div>}
            {declinePreviewError && <div role="alert" style={{ color: "#a4261b", marginBottom: 8 }}>{declinePreviewError}</div>}
            {declinePreview && (
              <div style={{ border: "1px solid #ddd", borderRadius: 6, padding: 12, maxHeight: 240, overflowY: "auto" }}>
                <div><b>Subject:</b> {declinePreview.subject}</div>
                <pre style={{ whiteSpace: "pre-wrap", font: "inherit", marginBottom: 0 }}>{declinePreview.text}</pre>
              </div>
            )}
            {declineError && <div role="alert" style={{ color: "#a4261b", marginTop: 12 }}>{declineError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={closeDeclineDialog} disabled={declining} style={{ padding: "8px 18px" }}>Cancel</button>
              <button
                onClick={submitDecline}
                disabled={declining || !declineReason || !declinePreview || declinePreviewLoading}
                style={{ padding: "8px 18px", background: declining ? "#aaa" : "#c0392b", color: "#fff", border: "none", borderRadius: 6 }}
              >
                {declining ? "Processing..." : "Decline Application"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Approved Student Modal (must be outside main component)
function ApprovedStudentModal({ student, onClose }) {
  const [showPass, setShowPass] = React.useState(false);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", padding: 32, borderRadius: 10, minWidth: 350, maxWidth: 500, boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
        <h3 style={{ marginBottom: 18 }}>Student Details</h3>
        <div style={{ marginBottom: 8 }}><b>ID Number:</b> {student.idNumber}</div>
        <div style={{ marginBottom: 8 }}><b>Name:</b> {student.firstName} {student.middleNameNA ? '' : student.middleName} {student.lastName}</div>
        <div style={{ marginBottom: 8 }}><b>Category:</b> {student.category}</div>
        <div style={{ marginBottom: 8 }}><b>Program:</b> {student.program}</div>
        <div style={{ marginBottom: 8 }}><b>Gender:</b> {student.gender}</div>
        <div style={{ marginBottom: 8 }}><b>Date of Birth:</b> {student.dob}</div>
        <div style={{ marginBottom: 8 }}><b>Mobile:</b> {student.mobile}</div>
        <div style={{ marginBottom: 8 }}><b>Email:</b> {student.email}</div>
        <div style={{ marginBottom: 8 }}><b>Submitted:</b> {formatDateTime(student.createdAt)}</div>
        <div style={{ marginBottom: 8 }}><b>2x2 Picture:</b> {student.picture && <a href={student.picture} target="_blank" rel="noopener noreferrer">View</a>}</div>
        <div style={{ marginBottom: 8 }}><b>Birth/Marriage Certificate:</b> {student.birthCert && <a href={student.birthCert} target="_blank" rel="noopener noreferrer">View</a>}</div>
        <div style={{ marginBottom: 8 }}><b>School/Govt ID:</b> {student.schoolId && <a href={student.schoolId} target="_blank" rel="noopener noreferrer">View</a>}</div>
        <div style={{ marginBottom: 8 }}><b>Copy of Grades:</b> {student.grades && <a href={student.grades} target="_blank" rel="noopener noreferrer">View</a>}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: "6px 22px", borderRadius: 6, background: "#e67e22", color: "#fff", border: "none" }}>Close</button>
        </div>
      </div>
    </div>
  );
}
